import * as React from 'react';

import {Button} from '../../button/button';
import {LoadingPage} from '../../loading-page/loading-page';

import {ColumnHeader} from './column-header';
import {ColumnHeaderReorderable} from './column-header-reorderable';
import type {Direction} from './column-header-reorderable';
import {ConfigurationsColumn} from './configurations-column';
import {GroupConfigurationsColumn} from './group-configurations-column';
import {canMoveLeft, canMoveRight} from './helpers';

import styles from './group-configuration-table.module.css';

export type Configuration = {
    id: string;
    label: string;
};

export type GroupConfigurationSetting = {
    id: string;
    isDisabled?: boolean;
    isFixed?: boolean;
};

export type GroupConfiguration = {
    id: string;
    name: string;
    icon: React.ComponentType<any> | null | undefined;
    isFixed?: boolean;
    configurations: GroupConfigurationSetting[];
};

type Props = {
    title: string | React.ReactNode;
    description: string;
    configurations: Configuration[];
    groupConfigurations: GroupConfiguration[] | null | undefined;
    onGroupConfigurationChange: (
        configurationId: string,
        groupId: string,
        isToggled: boolean
    ) => any;
    isUpdating?: boolean;
    createNew?: {
        onCreateNew: () => any;
        buttonText: string | React.ReactNode;
    };
    reorderable?: {
        onReorder: (newPosition: number, id: string) => any;
        moveRightTooltipText?: string;
        moveLeftTooltipText?: string;
        lockedTooltipText?: string | React.ReactNode;
    };
};

export function GroupConfigurationTable(props: Props) {
    const {
        title,
        description,
        configurations,
        groupConfigurations,
        onGroupConfigurationChange,
        isUpdating,
        createNew,
        reorderable,
    } = props;

    return (
        <div className={styles['group-configuration-table']}>
            <div className={styles['table-title']}>{title}</div>
            <div className={styles['table-content']}>
                <ConfigurationsColumn header={description} configurations={configurations} />
                <div className={styles['group-configurations-container']}>
                    {groupConfigurations ? (
                        groupConfigurations.map((groupConfiguration: GroupConfiguration, index) => (
                            <GroupConfigurationsColumn
                                key={groupConfiguration.id}
                                configurations={configurations}
                                header={
                                    reorderable ? (
                                        <ColumnHeaderReorderable
                                            text={groupConfiguration.name}
                                            onReorder={(direction: Direction) =>
                                                reorderable &&
                                                reorderable.onReorder(
                                                    direction === 'right' ? index + 1 : index - 1,
                                                    groupConfiguration.id
                                                )
                                            }
                                            canMoveRight={canMoveRight(index, groupConfigurations)}
                                            canMoveLeft={canMoveLeft(index, groupConfigurations)}
                                            isFixed={Boolean(groupConfiguration.isFixed)}
                                            moveRightTooltipText={reorderable.moveRightTooltipText}
                                            moveLeftTooltipText={reorderable.moveLeftTooltipText}
                                            lockedTooltipText={reorderable.lockedTooltipText}
                                        />
                                    ) : (
                                        <ColumnHeader
                                            text={groupConfiguration.name}
                                            icon={groupConfiguration.icon}
                                        />
                                    )
                                }
                                groupConfiguration={groupConfiguration}
                                onGroupConfigurationChange={onGroupConfigurationChange}
                                isUpdating={isUpdating}
                            />
                        ))
                    ) : (
                        <LoadingPage padding={0} />
                    )}
                    {createNew && (
                        <GroupConfigurationsColumn
                            configurations={configurations}
                            header={
                                <Button onClick={createNew.onCreateNew} variant='text-primary'>
                                    {createNew.buttonText}
                                </Button>
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
